import { useState } from 'react';
import { Card, Button, Input, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useDreGerencial, useAccountingCtx, type WaterfallItem } from './useAccounting';

/* ═══════════════════════════════════════════════════════════════════════════
   DRE com Fluxo de Resultado (Waterfall)
   ───────────────────────────────────────────────────────────────────────────
   Consome o MESMO endpoint da DRE Gerencial (o payload traz a série pronta):

   GET /accounting/dre?startDate&endDate  (AccountingReportsController)
   → DreGerencialResponseDto.waterfall: DreWaterfallItemDto[] {
       key: 'receita_bruta'|'deducoes'|'receita_liquida'|'custos_despesas'|'resultado',
       label, value, type: 'value'|'deduction'|'subtotal'|'total'
     }

   O backend já entrega a série com sinal: deduções/despesas vêm negativas;
   o gráfico abaixo é só apresentação (divs proporcionais — nada estimado).
   O tipo de cada barra (`value`/`deduction`/`subtotal`/`total`) define a cor
   e o conector do gráfico.
   ═══════════════════════════════════════════════════════════════════════════ */

function todayIso(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

function firstOfYearIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0] ?? '';
}

function formatAccounting(value: number): string {
  if (value < 0) return `(${formatCurrency(Math.abs(value))})`;
  return formatCurrency(value);
}

const STATE_HEIGHT = 220;

function WaterfallChart({ items }: { items: WaterfallItem[] }): JSX.Element {
  if (items.length === 0) {
    return <p className="wf-empty">Sem série waterfall para o período selecionado.</p>;
  }

  const maxAbs = Math.max(1, ...items.map((i) => Math.abs(i.value)));
  const zeroY = STATE_HEIGHT / 2;
  const scale = (STATE_HEIGHT / 2 - 24) / maxAbs;

  /* Altura da barra proporcional ao valor. Deduções/despesas saem negativas
     do backend: a barra desce do zero. Subtotal/total ainda podem ser zero
     (ex.: receita líquida 0) — a barra some, mas o rótulo fica. */
  const barHeight = (item: WaterfallItem): number =>
    Math.max(2, Math.abs(item.value) * scale);

  const barTop = (item: WaterfallItem): number =>
    item.value >= 0 ? zeroY - barHeight(item) : zeroY;

  const isNegative = (item: WaterfallItem): boolean => item.value < 0;
  const isTotal = (item: WaterfallItem): boolean => item.type === 'total';

  return (
    <div className="wf-chart" role="img" aria-label="Gráfico de fluxo de resultado do período">
      <div className="wf-chart__zero" style={{ top: zeroY }} />
      <div className="wf-chart__bars">
        {items.map((item, idx) => (
          <div key={item.key} className="wf-col">
            <div className="wf-col__value">
              <span className={isNegative(item) ? 'wf-val wf-val--neg' : 'wf-val'}>
                {formatAccounting(item.value)}
              </span>
            </div>
            <div className="wf-col__bar-wrap">
              <div
                className={[
                  'wf-bar',
                  item.type === 'total' ? 'wf-bar--total' : '',
                  item.type === 'subtotal' ? 'wf-bar--subtotal' : '',
                  isNegative(item) ? 'wf-bar--neg' : '',
                ].join(' ')}
                style={{ height: barHeight(item), top: barTop(item) }}
                title={`${item.label}: ${formatAccounting(item.value)}`}
              >
                <span className="wf-bar__label">{item.label}</span>
              </div>
              {idx < items.length - 1 && (
                <div className="wf-connector" style={{ top: zeroY }} />
              )}
            </div>
            <div className="wf-col__axis">
              {item.type === 'subtotal' || item.type === 'total' ? (
                <span className={isTotal(item) ? 'wf-axis wf-axis--strong' : 'wf-axis'}>{item.label}</span>
              ) : (
                <span className="wf-axis">{item.label}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="wf-legend">
        <span className="wf-legend__item"><span className="wf-swatch wf-swatch--value" /> Receita</span>
        <span className="wf-legend__item"><span className="wf-swatch wf-swatch--deduction" /> Dedução / Despesa</span>
        <span className="wf-legend__item"><span className="wf-swatch wf-swatch--subtotal" /> Subtotal</span>
        <span className="wf-legend__item"><span className="wf-swatch wf-swatch--total" /> Resultado</span>
      </div>
    </div>
  );
}

export function DreWaterfallPage(): JSX.Element {
  const [startDate, setStartDate] = useState(firstOfYearIso);
  const [endDate, setEndDate] = useState(todayIso);

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useDreGerencial(
    ctx,
    startDate || undefined,
    endDate || undefined,
  );

  const items = data?.waterfall ?? [];
  const hasLines =
    data && (data.revenue.length > 0 || data.expenses.length > 0);

  return (
    <div className="wf-page">
      <div className="mb-lg">
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          DRE — Fluxo de Resultado
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Waterfall funcional: receita bruta → deduções → receita líquida → custos e despesas → resultado.
        </p>
      </div>

      <Card padding="1rem" variant="outlined" className="wf-filters">
        <div className="wf-filters__row">
          <div className="wf-filters__field">
            <Input label="Data início" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="wf-filters__field">
            <Input label="Data fim" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
        </div>
      </Card>

      {isLoading && (
        <div className="wf-state">
          <LoadingSkeleton />
          <span>Carregando fluxo de resultado...</span>
        </div>
      )}
      {error && !isLoading && (
        <div className="wf-state wf-state--error">
          <p className="wf-state__title">Erro ao carregar DRE</p>
          <p className="wf-state__detail">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          <Card variant="outlined" padding="1.25rem" className="wf-chart-card">
            <div className="wf-card__head">
              <h3 className="wf-card__title">Fluxo de Resultado do Período</h3>
              <p className="wf-card__period">
                {formatDate(data.startDate)} a {formatDate(data.endDate)}
              </p>
            </div>
            <WaterfallChart items={items} />
          </Card>

          {!hasLines && items.length === 0 && (
            <div className="wf-state wf-state--empty">
              <p>Nenhum lançamento em conta de resultado no período selecionado.</p>
            </div>
          )}
        </>
      )}

      <style>{`
        .wf-page { padding: 1.5rem; }

        .wf-filters { margin-bottom: 1rem; }
        .wf-filters__row { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
        .wf-filters__field { flex: 0 0 180px; }

        .wf-chart-card { margin-bottom: 1rem; }
        .wf-card__head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
        .wf-card__title { margin: 0; font-size: 1.125rem; font-weight: 600; color: #191c1e; }
        .wf-card__period { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }

        .wf-chart { position: relative; padding: 0 0.5rem; }
        .wf-chart__zero { position: absolute; left: 0; right: 0; height: 1px; background: #191c1e; opacity: 0.25; }
        .wf-chart__bars { display: flex; align-items: stretch; gap: 0; position: relative; }

        .wf-col { flex: 1 1 0; display: flex; flex-direction: column; min-width: 0; }
        .wf-col__value { height: 26px; display: flex; align-items: flex-end; justify-content: center; }
        .wf-val { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 10px; color: #002045; white-space: nowrap; }
        .wf-val--neg { color: #ba1a1a; }

        .wf-col__bar-wrap { position: relative; height: ${STATE_HEIGHT}px; display: flex; justify-content: center; }
        .wf-bar { position: absolute; width: 46%; max-width: 64px; border-radius: 3px 3px 0 0; display: flex; align-items: flex-end; justify-content: center; background: #4f8f5a; border: 1px solid #2e5a38; }
        .wf-bar--neg { background: #e57373; border-color: #b33434; border-radius: 0 0 3px 3px; }
        .wf-bar--subtotal { background: #7fa8d9; border-color: #4877b0; }
        .wf-bar--total { background: #002045; border-color: #001b3c; }
        .wf-bar__label { display: none; }
        .wf-connector { position: absolute; width: 50%; left: 50%; height: 1px; background: #c4c6cf; }

        .wf-col__axis { height: 40px; display: flex; align-items: flex-start; justify-content: center; padding-top: 0.375rem; }
        .wf-axis { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 9px; letter-spacing: 0.02em; color: #43474e; text-align: center; line-height: 1.25; }
        .wf-axis--strong { font-weight: 700; color: #191c1e; }

        .wf-legend { display: flex; flex-wrap: wrap; gap: 1rem; margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #eceef0; }
        .wf-legend__item { display: inline-flex; align-items: center; gap: 0.375rem; font-size: 0.75rem; color: #43474e; }
        .wf-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; border: 1px solid rgba(0,0,0,0.2); }
        .wf-swatch--value { background: #4f8f5a; }
        .wf-swatch--deduction { background: #e57373; }
        .wf-swatch--subtotal { background: #7fa8d9; }
        .wf-swatch--total { background: #002045; }

        .wf-empty { margin: 0; padding: 2rem 1rem; text-align: center; color: #74777f; font-size: 0.875rem; }

        .wf-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; padding: 3rem 2rem; color: #43474e; font-size: 0.875rem; }
        .wf-state--error { color: #ba1a1a; }
        .wf-state--empty { color: #74777f; }
        .wf-state__title { margin: 0; font-weight: 600; }
        .wf-state__detail { margin: 0; font-size: 0.875rem; color: #74777f; }
      `}</style>
    </div>
  );
}